// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportDesignerCommonDataStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * Izenda Other Settings Store
 */
!(function () {
  var fetcher = require('fetchers/Izenda.ReportDesignerDataFetcher');
  var jsFormatService = require('share/Izenda.JsFormatService');
  var {DATA_TYPES} = require('logic/data/Izenda.ReportDesignerConstants');
  var {isNil, forEach, filter, map} = require('lodash/fp');

  var commonData = {
    DATA_TYPE: {}
  };

  /**
   * Get data from commonData based on dataType and key
   * @param  {[type]} dataType [description]
   * @param  {[type]} key      [description]
   * @return {[type]}          [description]
   */
  function getData(dataType, key) {
    return commonData[dataType] && commonData[dataType][key];
  }

  /**
   * Set data to commonData based on dataType, key and value
   * @param {[type]} dataType [description]
   * @param {[type]} key      [description]
   * @param {[type]} value    [description]
   */
  function setData(dataType, key, value) {
    commonData[dataType] = commonData[dataType] || {};
    commonData[dataType][key] = value;
  }

  /**
   * Generate cache key for RP_FUNCTION cache.
   */
  const functionQueryCacheKey = (functionMode, dataFormatType, tenantId, querySourceId) => {
    return `${functionMode}_${dataFormatType}_${tenantId}_${querySourceId}`
  };

  module.exports = {

    /**
     * Load list of function for report part: field properties, sub total, grand total
     * @param  {string} functionMode
     * @param  {string} dataFormatType
     * @param  {array} querySourceIds
     * @return {promise}
     */
    loadReportFunction: function (functionMode, dataFormatType, tenantId, querySourceIds, withoutCache) {
      const key = 'RP_FUNCTION';
      const cacheKey = functionQueryCacheKey(
        functionMode,
        dataFormatType,
        tenantId,
        querySourceIds && querySourceIds.join('_'));
      let cachedPromise;
      if (!withoutCache) {
        cachedPromise = getData(key, cacheKey);
      }

      // check if have cached or cached promise is rejected
      if (isNil(cachedPromise) || cachedPromise.isRejected()) {
        // cache promised object
        cachedPromise = fetcher.loadReportFunction(functionMode, dataFormatType, tenantId, querySourceIds);
        !withoutCache && setData(key, cacheKey, cachedPromise);
      }

      return cachedPromise.catch(function () {
        return [];
      });
    },

    /**
     * Load list of function for report part: field properties, sub total, grand total.
     * The result is cached.
     * @param {string} functionMode
     * @param {Object[]} dataTypeObjects
     * @param {string} tenantId
     * @returns {Promise} promise which returns array: [{dataType: ..., querySourceId: ..., data: [...]}, ...]
     */
    loadReportFunctions: function (functionMode: number, dataTypeObjects: Object[], tenantId: string) {
      const cacheName = 'RP_FUNCTION';

      // Get all not cached dataTypeObjects:
      const dataTypeObjectForQuery = filter(dtObject => {
        const cacheKey = functionQueryCacheKey(
          functionMode,
          dtObject.dataType,
          tenantId,
          dtObject.querySourceId);
        const cachedPromise = getData(cacheName, cacheKey);
        return isNil(cachedPromise) || cachedPromise.isRejected();
      }, dataTypeObjects);

      // The plan:
      // 1. Load functions which are missing in cache and put them into cache.
      // 2. Create array of promises for all datatypes (each promise returns function for its data type)
      // 3. Do Promise.all() to combine results into one array.
      const resultPromise = new Promise(resolve => {
        // Run the query to load missing data type functions:
        fetcher
          .loadReportFunctions(functionMode, dataTypeObjectForQuery, tenantId)

          // result is [{dataType: ..., querySourceId: ..., data: [...]}, ...]
          .then(result => {
            // Our query contains several results which could be used for function "loadReportFunction".
            // So, if we'll add those results into cache, we'll reduce number of queries 'function/...'
            // in future.
            forEach(resultItem => {
              const cacheKey = functionQueryCacheKey(
                functionMode,
                resultItem.dataType,
                tenantId,
                resultItem.querySourceId
              );
              const cachedPromise = getData(cacheName, cacheKey);
              if (isNil(cachedPromise) || cachedPromise.isRejected()) {
                setData(cacheName, cacheKey, Promise.resolve(resultItem.data));
              }
            }, result);
            return result;
          })

          // At this stage we have all dataType results cached.
          // Now we need to make retrieve from cache promises:
          .then(() =>
            map(dtObject => {
              const { dataType, querySourceId } = dtObject;
              const cacheKey = functionQueryCacheKey(functionMode, dataType, tenantId, querySourceId);
              return new Promise(r => {
                getData(cacheName, cacheKey).then(data => {
                  r({ dataType, querySourceId, data});
                });
              });
            }, dataTypeObjects)
          )

          // Here we're waiting for all cache promises and combine them all altogether
          // to make result array:
          .then(promises => {
            Promise.all(promises).then(resolve);
            return null;
          });
      });

      return resultPromise.catch(() => []);
    },

    /**
     * Load list of data format based on dataType for filter
     * @param  {string} dataFormatType
     * @return {promise}
     */
    loadFilterDataFormat: function (dataFormatType) {
      var key = 'FILTER_DATA_TYPE';
      var cachedPromise = getData(key, dataFormatType);

      if (isNil(cachedPromise) || cachedPromise.isRejected()) {
        // cache promised object
        cachedPromise = fetcher.loadFilterDataFormat(dataFormatType);
        setData(key, dataFormatType, cachedPromise);
      }

      return cachedPromise.catch(function () {
        return [];
      });
    },

    /**
     * Get list of data formats. The result is cached.
     * @param {string[]} dataFormatTypes
     * @returns {Promise} promise which returns array: [{dataType: ..., data: [...]}, ...]
     */
    loadReportFieldDataFormats: function (dataFormatTypes: string[]): any {
      const cacheName = 'RP_FIELD_DATA_TYPE';
      // Get all not cached dataTypeObjects:
      const dataFormatTypesForQuery = filter(dataType => {
        const cachedPromise = getData(cacheName, dataType);
        return isNil(cachedPromise) || cachedPromise.isRejected();
      }, dataFormatTypes);

      // The plan:
      // 1. Load format which are missing in cache and put them into cache.
      // 2. Create array of promises for all datatypes (each promise returns format for its data type)
      // 3. Do Promise.all() to combine results into one array.
      const resultPromise = new Promise(resolve => {
        fetcher.loadReportFieldDataFormats(dataFormatTypesForQuery)
        // result is [{dataType: ..., data: [...]}]
          .then(result => {
            // Our query contains several results which could be used for function "loadReportFieldDataFormat".
            // So, if we'll add those results into cache, we'll reduce number of queries 'field/dataFormat...'
            // in future.
            forEach(resultItem => {
              let cachedPromise = getData(cacheName, resultItem.dataType);
              if (isNil(cachedPromise) || cachedPromise.isRejected()) {
                setData(cacheName, resultItem.dataType, Promise.resolve(resultItem.data));
              }
            }, result);
            return result;
          })
          // At this stage we have all dataType results cached.
          // Now we need to make retrieve from cache promises:
          .then(() =>
            map(dataType => {
              return new Promise(r => {
                getData(cacheName, dataType).then(data => {
                  r({ dataType, data});
                });
              });
            }, dataFormatTypes)
          )

          // Here we're waiting for all cache promises and combine them all altogether
          // to make result array:
          .then(promises => {
            Promise.all(promises).then(resolve);
            return null;
          });
      });

      return resultPromise.catch(() => []);
    },

    /**
     * Get list of data-format for report part, field properties
     * @param  {[type]} dataFormatType [description]
     * @return {[type]}                [description]
     */
    loadReportFieldDataFormat: function (dataFormatType, withoutCache) {
      var key = 'RP_FIELD_DATA_TYPE';
      var cachedPromise;

      if (!withoutCache) {
        cachedPromise = getData(key, dataFormatType);
      }

      // check if have cached or cached promise is rejected
      if (isNil(cachedPromise) || cachedPromise.isRejected()) {
        // cache promised object
        cachedPromise = fetcher.loadReportFieldDataFormat(dataFormatType);
        !withoutCache && setData(key, dataFormatType, cachedPromise);
      }

      return cachedPromise.catch(function () {
        return [];
      });
    },

    /**
     * Get list of custom data-format for report part, field properties
     * @param  {[type]} dataFormatType [description]
     * @return {[type]}                [description]
     */
    loadCustomDataFormat: function (dataFormatType, withoutCache) {
      var key = 'RP_FIELD_DATA_TYPE';
      var cachedPromise;

      if (!withoutCache) {
        cachedPromise = getData(key, dataFormatType);
      }

      if (isNil(cachedPromise) || cachedPromise.isRejected()) {
        cachedPromise = fetcher.loadReportFieldDataFormat(DATA_TYPES.CUSTOM).then((data) => {
          const customDataFormats = filter(item => item.jsFormatString !== null, data);
          forEach(customDataFormat => {
            jsFormatService.registerJsFormatString(customDataFormat.name, customDataFormat.jsFormatString);
          }, customDataFormats);
        });
        !withoutCache && setData(key, dataFormatType, cachedPromise);
      }

      return cachedPromise.catch(function () {
        return [];
      });
    },
  };
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.ReportDesignerCommonDataStore.js